/**
 * urgencyStyle — canonical mapping urgency → {label, colour class, icon}.
 * Reused by ActionCard AND PriorityAiPanel so the two surfaces stay
 * visually consistent (same colour + same icon for the same urgency).
 *
 * Kept as pure JS module (no JSX) so tests can import it without any
 * React/Phosphor setup. `iconKey` returns a Phosphor component NAME as
 * a string; consumers import the actual Phosphor icon themselves.
 */

/** Presentation metadata per urgency bucket. */
const URGENCY_STYLE = {
  CRITICAL: {
    label: { es: "Crítica", en: "Critical" },
    // Tailwind classes reused across the code base (see AlertsBoard for
    // the same red/amber/emerald palette).
    color: "text-red-700 dark:text-red-300",
    bg: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-300 dark:border-red-800",
    badge: "bg-red-600 text-white",
    // Franja izquierda — el mismo recurso que usa el tablero de Seguimientos
    // para decir la urgencia antes de que leas una sola palabra.
    stripe: "border-l-red-500",
    iconKey: "WarningOctagon",
  },
  TODAY: {
    label: { es: "Hoy", en: "Today" },
    color: "text-amber-800 dark:text-amber-200",
    bg: "bg-amber-50 dark:bg-amber-900/20",
    border: "border-amber-300 dark:border-amber-800",
    badge: "bg-amber-500 text-white",
    stripe: "border-l-amber-500",
    iconKey: "Clock",
  },
  OVERDUE: {
    label: { es: "Vencida", en: "Overdue" },
    color: "text-orange-800 dark:text-orange-200",
    bg: "bg-orange-50 dark:bg-orange-900/20",
    border: "border-orange-300 dark:border-orange-800",
    badge: "bg-orange-600 text-white",
    stripe: "border-l-orange-500",
    iconKey: "Warning",
  },
  UPCOMING: {
    label: { es: "Próxima", en: "Upcoming" },
    color: "text-stone-700 dark:text-stone-300",
    bg: "bg-stone-50 dark:bg-stone-900/40",
    border: "border-stone-300 dark:border-stone-700",
    badge: "bg-stone-500 text-white",
    stripe: "border-l-stone-300 dark:border-l-stone-700",
    iconKey: "CalendarBlank",
  },
  INFO: {
    label: { es: "Info", en: "Info" },
    color: "text-stone-600 dark:text-stone-400",
    bg: "bg-stone-50/50 dark:bg-stone-900/20",
    border: "border-stone-200 dark:border-stone-800",
    badge: "bg-stone-400 text-white",
    stripe: "border-l-stone-200 dark:border-l-stone-800",
    iconKey: "Info",
  },
};

const DEFAULT = URGENCY_STYLE.INFO;

export function urgencyStyleFor(urgency) {
  return URGENCY_STYLE[urgency] || DEFAULT;
}

export function urgencyLabel(urgency, lang = "es") {
  const s = urgencyStyleFor(urgency);
  return s.label[lang] || s.label.es;
}

/** Order used by chip rows in the UI. */
export const URGENCY_CHIP_ORDER = ["CRITICAL", "TODAY", "OVERDUE", "UPCOMING"];

/**
 * Types the Action Center tabs surface. Matches the backend
 * `action_item.type` enum minus reserved PMS types. `all` is a UI-only
 * pseudo-tab handled by the caller.
 */
export const TYPE_TAB_ORDER = [
  { key: "all",          label: { es: "Todo",       en: "All" } },
  { key: "follow_up",    label: { es: "Leads",      en: "Leads" } },
  // Filtro de interfaz, no un tipo del backend: acota "Leads" al
  // seguimiento comercial programado y deja fuera las tarjetas de
  // "responder a un mensaje entrante", que son otro trabajo.
  // Ver lib/actionFilters.js.
  { key: "followups",    label: { es: "Seguimientos", en: "Follow-ups" } },
  { key: "payment",      label: { es: "Pagos",      en: "Payments" } },
  { key: "construction", label: { es: "Obra",       en: "Construction" } },
  { key: "document",     label: { es: "Documentos", en: "Documents" } },
  // Mismo nombre que en el menú lateral: las tareas que crea la IA se
  // leen aquí como acciones, y llamarlas de dos maneras distintas hacía
  // dudar de si eran lo mismo.
  { key: "task",         label: { es: "Tareas IA",  en: "AI tasks" } },
  // Excepciones del motor de automatización comercial. NO lleva los envíos
  // que salieron bien: una pestaña de "automatización" llena de éxitos deja
  // de leerse, y el Centro de acción contesta qué necesita atención.
  { key: "automation",   label: { es: "Automatización", en: "Automation" } },
];

/**
 * Human-readable "time until/since" for a due_at. Returns a short label
 * ("hoy 09:00", "vence en 5h", "vencido hace 2 días"). Empty string if
 * `due_at` is null/invalid.
 */
export function humanizeDueAt(dueAt, now = new Date(), lang = "es") {
  if (!dueAt) return "";
  const dt = new Date(dueAt);
  if (Number.isNaN(dt.getTime())) return "";
  const deltaMs = dt.getTime() - now.getTime();
  const absMin = Math.abs(deltaMs) / 60000;
  const absH = absMin / 60;
  const absD = absH / 24;

  if (lang === "en") {
    if (deltaMs >= 0) {
      if (absMin < 60) return `in ${Math.max(1, Math.round(absMin))} min`;
      if (absH < 24) return `in ${Math.round(absH)}h`;
      return `in ${Math.round(absD)}d`;
    } else {
      if (absMin < 60) return `${Math.max(1, Math.round(absMin))} min ago`;
      if (absH < 24) return `${Math.round(absH)}h ago`;
      return `${Math.round(absD)}d ago`;
    }
  }
  if (deltaMs >= 0) {
    if (absMin < 60) return `en ${Math.max(1, Math.round(absMin))} min`;
    if (absH < 24) return `en ${Math.round(absH)}h`;
    return `en ${Math.round(absD)} día(s)`;
  }
  if (absMin < 60) return `hace ${Math.max(1, Math.round(absMin))} min`;
  if (absH < 24) return `hace ${Math.round(absH)}h`;
  return `hace ${Math.round(absD)} día(s)`;
}
